import { useState, useRef, useEffect, useMemo } from "react";
import { MagnifyingGlass, Buildings, CaretUpDown, Check, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import axios from "axios";

export const CompanyCombobox = ({ value, onChange, placeholder = "Seleccionar empresa...", className = "" }) => {
  const { token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    const fetchCompanies = async () => {
      try {
        const res = await axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } });
        setCompanies(res.data || []);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    };
    fetchCompanies();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return companies;
    const term = search.toLowerCase();
    return companies.filter((c) => c.toLowerCase().includes(term));
  }, [companies, search]);

  const handleSelect = (company) => {
    onChange(company);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const showCustomOption = search.trim() && !companies.some((c) => c.toLowerCase() === search.trim().toLowerCase());

  return (
    <div className={`relative ${className}`} ref={containerRef} data-testid="company-combobox">
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center w-full h-10 px-3 border border-slate-200 rounded-md bg-white text-sm text-left hover:border-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        data-testid="company-combobox-trigger"
      >
        <Buildings size={16} className="text-slate-400 mr-2 flex-shrink-0" />
        <span className={`flex-1 truncate ${value ? "text-slate-900" : "text-slate-400"}`}>
          {value || placeholder}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            className="p-0.5 hover:bg-slate-100 rounded mr-1"
            data-testid="company-clear-btn"
          >
            <X size={14} className="text-slate-400" />
          </span>
        )}
        <CaretUpDown size={14} className="text-slate-400 flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden" data-testid="company-combobox-dropdown">
          {/* Search input */}
          <div className="flex items-center px-3 py-2 border-b border-slate-100">
            <MagnifyingGlass size={16} className="text-slate-400 mr-2 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar o escribir empresa..."
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
              data-testid="company-combobox-search"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="p-0.5 hover:bg-slate-100 rounded">
                <X size={14} className="text-slate-400" />
              </button>
            )}
          </div>

          {/* Options list */}
          <div className="max-h-[240px] overflow-y-auto" data-testid="company-combobox-list">
            {/* Custom/new option */}
            {showCustomOption && (
              <button
                type="button"
                onClick={() => handleSelect(search.trim())}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-blue-50 transition-colors border-b border-slate-50"
                data-testid="company-combobox-custom"
              >
                <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Buildings size={12} className="text-blue-600" />
                </span>
                <span className="flex-1 truncate">
                  <span className="text-blue-600 font-medium">Agregar:</span>{" "}
                  <span className="text-slate-700">{search.trim()}</span>
                </span>
              </button>
            )}

            {filtered.length === 0 && !showCustomOption && (
              <div className="px-3 py-6 text-center text-slate-400 text-sm">
                Sin resultados
              </div>
            )}

            {filtered.map((company) => (
              <button
                key={company}
                type="button"
                onClick={() => handleSelect(company)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-slate-50 transition-colors ${
                  value === company ? "bg-blue-50/60" : ""
                }`}
                data-testid={`company-option-${company}`}
              >
                <Buildings size={14} className="text-slate-400 flex-shrink-0" />
                <span className="flex-1 truncate text-slate-700">{company}</span>
                {value === company && <Check size={16} className="text-blue-600 flex-shrink-0" weight="bold" />}
              </button>
            ))}
          </div>

          {/* Footer with count */}
          <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/50">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">
              {filtered.length} empresa{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
